import React, { useRef, useState } from 'react';
import TextArea from 'devextreme-react/text-area';
import { Validator, RequiredRule } from 'devextreme-react/validator';
import { ValidatedEvent } from 'devextreme/ui/validator';
import { PasteEvent } from 'devextreme/ui/text_box';
import { InfoIconButton } from './InfoIconButton';

export enum MultilineTextBoxComponentSpacing {
    None = 'noSpacing',
    SmallSpacing = 'smallSpacing',
    StandardSpacing = 'standardSpacing',
    LargeSpacing = 'largeSpacing',
}

export enum MultilineTextBoxLabelSize {
    None,
    Small,
    Large,
}

interface MultilineTextBoxProps {
    id?: string;
    value?: string;
    readOnly?: boolean;
    onChange: (value: string) => void;
    label?: string;
    helperText?: string;
    required?: boolean;
    children?: React.ReactNode;
    validator?: React.ReactNode;
    width?: string;
    height?: string;
    maxLength?: number;
    placeholder?: string;
    onPaste?: (e: PasteEvent) => void;
    componentSpacing?: MultilineTextBoxComponentSpacing;
    labelSize?: MultilineTextBoxLabelSize;
    validateOnFocusChange?: boolean;
    showInfoIcon?: boolean;
    infoText?: string;
}

export const MultilineTextBox: React.FC<MultilineTextBoxProps> = ({
    id,
    value,
    readOnly = false,
    onChange,
    label,
    helperText,
    required = false,
    children,
    validator,
    width = 'auto',
    height = 'auto',
    maxLength,
    placeholder,
    onPaste,
    componentSpacing = MultilineTextBoxComponentSpacing.StandardSpacing,
    labelSize = MultilineTextBoxLabelSize.None,
    validateOnFocusChange = true,
    showInfoIcon = false,
    infoText,
}) => {
    const [errorMessage, setErrorMessage] = useState('');
    const [fieldLengthError, set_fieldLengthError] = useState(false);
    const validatorRef = useRef<any>(null);

    const handleValueChanged = (e: any) => {
        onChange(e.value);
    };

    const onValidated = (validatedInfo: ValidatedEvent) => {
        setErrorMessage(validatedInfo?.brokenRule?.message ?? '');
    };

    const handleFocus = () => {
        if (validatorRef.current) {
            const result = validatorRef.current.instance.validate();
            if (!result.isValid) {
                // Assuming the first broken rule's message is the error message:
                setErrorMessage(result.brokenRules[0]?.message || '');
            } else {
                setErrorMessage('');
            }
        }
    };

    const onKeyUp = (e: any) => {
        const value = e.event.currentTarget.value;

        if (maxLength && value.length >= maxLength) {
            set_fieldLengthError(true);
        } else {
            set_fieldLengthError(false);
        }
    };

    const getLabelClassName = () => {
        switch (labelSize) {
            case MultilineTextBoxLabelSize.Large:
                return ' large';
            default:
                return '';
        }
    };

    const getControlSpacingTop = () => {
        switch (labelSize) {
            case MultilineTextBoxLabelSize.Large:
                return '22px';
            default:
                return;
        }
    };

    const getControlSpacingBottom = () => {
        switch (labelSize) {
            case MultilineTextBoxLabelSize.Large:
                return '20px';
            default:
                return;
        }
    };

    return (
        <div
            className={`textarea-container ${componentSpacing}`}
            style={{ width: width }}
        >
            {label && (
                <div>
                    <label
                        className={`textarea-label${getLabelClassName()}${errorMessage ? ' error' : ''}`}
                        style={{ marginTop: getControlSpacingTop(), marginBottom: getControlSpacingBottom() }}
                    >
                        {required ? label + '*' : label}
                    </label>
                    {showInfoIcon && <InfoIconButton modalText={infoText} />}
                </div>
            )}
            <div
                className='textarea-wrapper'
                style={{ position: 'relative' }}
            >
                <TextArea
                    id={id}
                    value={value}
                    valueChangeEvent='change'
                    readOnly={readOnly}
                    onValueChanged={handleValueChanged}
                    onFocusIn={validateOnFocusChange ? handleFocus : undefined}
                    onFocusOut={validateOnFocusChange ? handleFocus : undefined}
                    onKeyUp={onKeyUp}
                    className={`textarea-input ${errorMessage ? 'error' : ''}`}
                    width={width}
                    height={height}
                    maxLength={maxLength}
                    placeholder={placeholder}
                    onPaste={onPaste}
                >
                    <Validator
                        ref={validatorRef}
                        onValidated={onValidated}
                    >
                        {required && <RequiredRule message='This field is required' />}
                        {validator}
                    </Validator>
                </TextArea>
            </div>
            {/* Show helper text only if there is no error */}
            {errorMessage ? (
                <div className='textarea-error'>{errorMessage}</div>
            ) : maxLength && fieldLengthError ? (
                <div className='textarea-error'>This field will not allow more than {maxLength} characters. </div>
            ) : (
                helperText && <div className='textarea-helper'>{helperText}</div>
            )}
            {children && <div className='textarea-children'>{children}</div>}
        </div>
    );
};
